import "./index.scss";

import { Box, CircularProgress, Grid, Modal, Stack } from "@mui/material";
import { ReactComponent as IconWorkingOutOff } from "assets/images/side-menu/members-working-out-off.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import { ReactComponent as UncheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/off-normal.svg";
import { ReactComponent as CheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/on-normal.svg";
import ScreenLoader from "components/ScreenLoader";
import SearchBar from "components/SearchBar";
import { MSG_OPERATION_FAILED } from "constants/index";
import { stringToColor } from "helpers/utils";
import { TPagination } from "modules/api/type";
import { getWorkoutPlanList } from "modules/workout-plan/workoutPlanServices";
import { memo, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const CopyFromMemberModal = (props: { open: boolean; onClose: any }) => {
  const { open, onClose } = props;
  const [loading, setLoading] = useState(false);
  const [searchStr, setSearchStr] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [params, setParams] = useState({
    limit: 10,
    page: 1,
    search: "",
    sort_by: "NAME",
    sort_order: "ASC",
  });
  const [pagination, setPagination] = useState<TPagination>();

  const fetchData = async (parameters) => {
    if (loading) return;
    setLoading(true);
    getWorkoutPlanList(parameters)
      .then((res) => {
        if (res.success) {
          const data = parameters.page === 1 ? res.data : [...filterOptions, ...res.data];
          setParams(parameters);
          setFilterOptions(data);
          setPagination(res.pagination);
        } else {
          throw new Error();
        }
      })
      .catch((e) => {
        console.log(e);
        toast(MSG_OPERATION_FAILED, { type: "error" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(params);
  }, []);

  //#region event handlers
  const handleCloseModal = useCallback(() => {
    onClose();
  }, []);

  const handleSearch = (str: string) => {
    setFilterOptions([]);
    setPagination(null);
    const tempParams = { ...params, page: 1, search: str };
    setSearchStr(str);
    fetchData(tempParams);
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loading) {
      if (params.page < pagination.total_pages) {
        const tempParams = { ...params, page: params.page + 1 };
        fetchData(tempParams);
      }
    }
  };
  //#endregion event handlers

  return (
    <Modal
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      open={open}
      onClose={handleCloseModal}
    >
      <Stack className="personal-details-modal-container copy-modal">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="40px"
          className="header"
        >
          <h1>Copy from Template</h1>
          <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
        </Stack>
        <Box className={`searchable-list-container`}>
          <Box className="header">
            <SearchBar
              isAsync={true}
              className="search-bar"
              value={searchStr}
              onSearch={handleSearch}
              placeholder="Press enter to search"
            />
          </Box>
          <Box className="list" onScroll={handleScroll}>
            {filterOptions.map((item, index) => (
              <button
                className={`checkable-button`}
                key={`copyFromMember${index}`}
                onClick={() => onClose(item)}
              >
                <Grid container spacing={8}>
                  <Grid item xs={1}>
                    <span
                      style={{
                        backgroundColor: stringToColor(item.name),
                      }}
                      className={"rounded-circle member-icon"}
                    >
                      <IconWorkingOutOff className="icon-white size16" />
                    </span>
                  </Grid>
                  <Grid item xs={10}>
                    {item.name}
                  </Grid>
                  <Grid item xs={1} className="check">
                    <CheckedCircleIcon className="showOnHover" />
                    <UncheckedCircleIcon className="showOnMouseOut" />
                  </Grid>
                  <Grid item xs={1}></Grid>
                  <Grid item xs={11} className="templateProgramIncludes">
                    {item.has_stretch_workouts && <span>&nbsp;Flexibility Program</span>}
                    {item.has_cardio_workouts && <span>&nbsp;Cardio Program</span>}
                    {item.has_strength_workouts && <span>&nbsp;Strength Program</span>}
                  </Grid>
                </Grid>
              </button>
            ))}
            {loading && (
              <Stack alignItems={"center"}>
                <CircularProgress disableShrink color="success" />
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>
    </Modal>
  );
};

export default memo(CopyFromMemberModal);
